// Iter 71 — Vendor Management Phase 1 — shared labels.
// Single source of truth for human-readable strings displayed in the
// vendor pages. Backend canonicalises enums in snake_case; this map
// hand-crafts the display strings so we can localise / capitalise
// consistently without scattering switch-statements through 3 files.

export const CATEGORY_LABEL = {
    development_partner: "Development Partner",
    infrastructure_provider: "Infrastructure Provider",
    security_auditor: "Security Auditor",
    kyc_provider: "KYC Provider",
    data_provider: "Data Provider",
    hosting_provider: "Hosting Provider",
    software_vendor: "Software Vendor",
    marketing_vendor: "Marketing Vendor",
    consultant: "Consultant",
    legal_compliance: "Legal / Compliance",
    other: "Other",
};

export const VENDOR_TYPE_LABEL = {
    company: "Company",
    individual: "Individual",
    partnership: "Partnership",
    llp: "LLP",
    proprietorship: "Proprietorship",
    government: "Government",
};

export const MSME_LABEL = {
    not_registered: "Not Registered",
    registered_micro: "Micro",
    registered_small: "Small",
    registered_medium: "Medium",
};

export const PAYMENT_METHOD_LABEL = {
    bank_transfer: "Bank Transfer",
    upi: "UPI",
    cheque: "Cheque",
    credit_card: "Credit Card",
    other: "Other",
};

export const TDS_LABEL = {
    "194J": "194J — Professional / Technical",
    "194C": "194C — Contractor",
    "194H": "194H — Commission / Brokerage",
    "194Q": "194Q — Purchase of Goods",
    not_applicable: "Not Applicable",
};

export const DOC_TYPE_LABEL = {
    gst_certificate: "GST Certificate",
    pan_copy: "PAN Copy",
    msme_certificate: "MSME Certificate",
    agreement: "Agreement",
    nda: "NDA",
    quotation: "Quotation",
    invoice: "Invoice",
    bank_proof: "Bank Proof",
    other: "Other",
};

// Helpful list of every enum we read from the backend so the form
// pages can short-circuit and use these defaults if the meta/enums
// fetch hasn't resolved yet (no jarring empty dropdowns).
export const DEFAULT_ENUMS = {
    categories: Object.keys(CATEGORY_LABEL),
    vendor_types: Object.keys(VENDOR_TYPE_LABEL),
    msme_statuses: Object.keys(MSME_LABEL),
    currencies: ["INR", "USD", "EUR", "GBP", "AED", "SGD"],
    payment_methods: Object.keys(PAYMENT_METHOD_LABEL),
    tds_categories: Object.keys(TDS_LABEL),
    document_types: Object.keys(DOC_TYPE_LABEL),
    indian_states: [],
};

export function humanBytes(n) {
    if (!n && n !== 0) return "";
    const u = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

// ──────────────────────────────────────────────────────────────────────
// Iter 72 — Vendor Services / Contracts labels
// ──────────────────────────────────────────────────────────────────────
export const BILLING_FREQUENCY_LABEL = {
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    half_yearly: "Half-Yearly",
    annual: "Annual",
    biennial: "Biennial",
    one_time: "One-Time",
    custom: "Custom",
};

export const SERVICE_STATUS_LABEL = {
    active: "Active",
    paused: "Paused",
    pending_renewal: "Pending Renewal",
    expired: "Expired",
    cancelled: "Cancelled",
};

export const SERVICE_STATUS_BADGE = {
    active: { className: "bg-emerald-100 text-emerald-700", label: "Active" },
    paused: { className: "bg-amber-100 text-amber-700", label: "Paused" },
    pending_renewal: { className: "bg-violet-100 text-violet-700", label: "Pending Renewal" },
    expired: { className: "bg-rose-100 text-rose-700", label: "Expired" },
    cancelled: { className: "bg-slate-100 text-slate-500", label: "Cancelled" },
};

export const SERVICE_CATEGORY_LABEL = {
    hosting: "Hosting",
    domain: "Domain",
    ssl: "SSL",
    cloud: "Cloud",
    software_license: "Software License",
    consulting: "Consulting",
    compliance: "Compliance",
    audit: "Audit",
    marketing: "Marketing",
    other: "Other",
};

export const SERVICE_DEFAULT_ENUMS = {
    billing_frequencies: Object.keys(BILLING_FREQUENCY_LABEL),
    statuses: Object.keys(SERVICE_STATUS_LABEL),
    service_categories: Object.keys(SERVICE_CATEGORY_LABEL),
    currencies: ["INR", "USD", "EUR", "GBP", "AED", "SGD"],
    default_renewal_notice_days: 30,
    renewal_notice_presets: [30, 60, 90],
};

export function formatAmount(amount, currency = "INR") {
    const n = Number(amount);
    if (Number.isNaN(n)) return "—";
    try {
        return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(n);
    } catch {
        return `${currency} ${n.toLocaleString()}`;
    }
}

// ──────────────────────────────────────────────────────────────────────
// Iter 73 — Vendor Quotations labels
// ──────────────────────────────────────────────────────────────────────
export const QUOTATION_STATUS_LABEL = {
    draft: "Draft",
    received: "Received",
    under_review: "Under Review",
    accepted: "Accepted",
    rejected: "Rejected",
    expired: "Expired",
    converted: "Converted to PO",
};

export const QUOTATION_STATUS_BADGE = {
    draft: { className: "bg-slate-100 text-slate-600", label: "Draft" },
    received: { className: "bg-sky-100 text-sky-700", label: "Received" },
    under_review: { className: "bg-amber-100 text-amber-700", label: "Under Review" },
    accepted: { className: "bg-emerald-100 text-emerald-700", label: "Accepted" },
    rejected: { className: "bg-rose-100 text-rose-700", label: "Rejected" },
    expired: { className: "bg-zinc-200 text-zinc-700", label: "Expired" },
    converted: { className: "bg-violet-100 text-violet-700", label: "Converted" },
};

export const QUOTATION_DEFAULT_ENUMS = {
    statuses: Object.keys(QUOTATION_STATUS_LABEL),
    currencies: ["INR", "USD", "EUR", "GBP", "AED", "SGD"],
    terminal_states: ["rejected", "expired", "converted"],
    locked_states: ["accepted", "rejected", "expired", "converted"],
};

export function isQuotationLocked(status) {
    return QUOTATION_DEFAULT_ENUMS.locked_states.includes(status);
}

export function formatMarginPct(pct) {
    const n = Number(pct);
    if (!Number.isFinite(n)) return "—";
    return `${n.toFixed(2)}%`;
}

// ──────────────────────────────────────────────────────────────────────
// Iter 74 — Vendor Purchase Orders labels
// ──────────────────────────────────────────────────────────────────────
export const PO_STATUS_LABEL = {
    draft: "Draft",
    issued: "Issued",
    partially_billed: "Partially Billed",
    billed: "Billed",
    cancelled: "Cancelled",
};

export const PO_STATUS_BADGE = {
    draft: { className: "bg-slate-100 text-slate-600", label: "Draft" },
    issued: { className: "bg-sky-100 text-sky-700", label: "Issued" },
    partially_billed: { className: "bg-amber-100 text-amber-700", label: "Partially Billed" },
    billed: { className: "bg-emerald-100 text-emerald-700", label: "Billed" },
    cancelled: { className: "bg-rose-100 text-rose-700", label: "Cancelled" },
};

export const PO_DEFAULT_ENUMS = {
    statuses: Object.keys(PO_STATUS_LABEL),
    locked_states: ["issued", "partially_billed", "billed", "cancelled"],
    derived_states: ["partially_billed", "billed"],
};

export function isPoLocked(status) {
    return PO_DEFAULT_ENUMS.locked_states.includes(status);
}

// ──────────────────────────────────────────────────────────────────────
// Iter 75 — Vendor Bills labels
// ──────────────────────────────────────────────────────────────────────
export const BILL_STATUS_LABEL = {
    pending: "Pending",
    partially_paid: "Partially Paid",
    paid: "Paid",
    cancelled: "Cancelled",
};

export const BILL_STATUS_BADGE = {
    pending: { className: "bg-amber-100 text-amber-700", label: "Pending" },
    partially_paid: { className: "bg-sky-100 text-sky-700", label: "Partially Paid" },
    paid: { className: "bg-emerald-100 text-emerald-700", label: "Paid" },
    cancelled: { className: "bg-rose-100 text-rose-700", label: "Cancelled" },
};

export const BILL_DEFAULT_ENUMS = {
    statuses: Object.keys(BILL_STATUS_LABEL),
    derived_states: ["partially_paid", "paid"],
    tds_sections: ["194J", "194C", "194H", "194Q", "not_applicable"],
};

// ──────────────────────────────────────────────────────────────────────
// Iter 76 — Vendor Payments labels
// ──────────────────────────────────────────────────────────────────────
export const PAYMENT_MODES = ["NEFT", "RTGS", "IMPS", "UPI", "Cheque", "Cash", "Other"];

// ──────────────────────────────────────────────────────────────────────
// Iter 77 — Vendor Service Invoices labels (lightweight per-service tracker)
// ──────────────────────────────────────────────────────────────────────
export const SERVICE_INVOICE_STATUS_LABEL = {
    unpaid: "Unpaid",
    paid: "Paid",
    cancelled: "Cancelled",
};

export const SERVICE_INVOICE_STATUS_BADGE = {
    unpaid: { className: "bg-amber-100 text-amber-700", label: "Unpaid" },
    paid: { className: "bg-emerald-100 text-emerald-700", label: "Paid" },
    cancelled: { className: "bg-rose-100 text-rose-700", label: "Cancelled" },
};
